import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps wide tables so narrow viewports can scroll horizontally.
 * Kept intentionally lightweight to avoid scroll jank on long admin pages.
 */
const DashboardTableScroll = ({
  children,
  hintText = "Scroll sideways to see all columns (price, actions, etc.)",
  className = "",
}) => {
  const scrollRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const next = el.scrollWidth > el.clientWidth + 8;
    setHasOverflow((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    window.addEventListener("resize", checkOverflow, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow]);

  return (
    <div className={`relative w-full min-w-0 ${className}`}>
      <div
        ref={scrollRef}
        className="overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]"
        tabIndex={0}
        role="region"
        aria-label={hintText}
      >
        {children}
      </div>
      {hasOverflow ? (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1 rounded-md bg-[#38513b]/10 px-2 py-1 font-semibold text-[#38513b]">
            <span aria-hidden>→</span>
            {hintText}
          </span>
        </p>
      ) : null}
    </div>
  );
};

export default DashboardTableScroll;
